/**
 * Match hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { matchesApi, ApiError } from '../lib/api';
import { useAppStore } from '../stores/appStore';
import type { Match, PaginatedResponse } from '../../shared/types';

// Query keys
export const matchKeys = {
  all: ['matches'] as const,
  lists: () => [...matchKeys.all, 'list'] as const,
  list: (filters?: object) => [...matchKeys.lists(), filters] as const,
  upcomings: () => [...matchKeys.all, 'upcoming'] as const,
  upcoming: (params?: object) => [...matchKeys.all, 'upcoming', params] as const,
  lives: () => [...matchKeys.all, 'live'] as const,
  live: (params?: object) => [...matchKeys.all, 'live', params] as const,
  details: () => [...matchKeys.all, 'detail'] as const,
  detailById: (id: number) => [...matchKeys.details(), id] as const,
  detail: (id: number, params?: object) => [...matchKeys.detailById(id), params] as const,
};

interface MatchFilters {
  stage?: string;
  status?: string;
  date?: string;
  page?: number;
  limit?: number;
  isDemo?: boolean;
}

/**
 * Hook to list matches with filters
 */
export function useMatches(filters?: MatchFilters) {
  return useMatchesWithOptions(filters);
}

export function useMatchesWithOptions(filters?: MatchFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: matchKeys.list(filters),
    queryFn: async () => {
      const response = await matchesApi.list(filters);
      return response as PaginatedResponse<Match>;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get upcoming matches
 */
export function useUpcomingMatches(params?: { isDemo?: boolean }) {
  return useQuery({
    queryKey: matchKeys.upcoming(params),
    queryFn: async () => {
      const response = await matchesApi.upcoming(params);
      return response.data as Match[];
    },
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook to get live matches
 */
export function useLiveMatches(params?: { isDemo?: boolean }) {
  return useQuery({
    queryKey: matchKeys.live(params),
    queryFn: async () => {
      const response = await matchesApi.live(params);
      return response.data as Match[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds for live matches
  });
}

/**
 * Hook to get single match
 */
export function useMatch(id: number, params?: { isDemo?: boolean }) {
  return useQuery({
    queryKey: matchKeys.detail(id, params),
    queryFn: async () => {
      const response = await matchesApi.get(id, params);
      return response.data as Match;
    },
    enabled: !!id,
  });
}

/**
 * Hook to update match score (admin only)
 */
export function useUpdateMatchScore() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({
      id,
      homeScore,
      awayScore,
      status,
    }: {
      id: number;
      homeScore: number;
      awayScore: number;
      status?: string;
    }) => matchesApi.updateScore(id, homeScore, awayScore, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: matchKeys.all });
      queryClient.invalidateQueries({ queryKey: matchKeys.detailById(variables.id) });
      // Also invalidate pool rankings as they may have changed
      queryClient.invalidateQueries({ queryKey: ['pools', 'ranking'] });
      addNotification({
        type: 'success',
        message: 'Resultado atualizado e rankings recalculados!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao atualizar resultado',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to recalculate all matches (admin only)
 */
export function useRecalculateMatches() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: () => matchesApi.recalculate(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: matchKeys.all });
      queryClient.invalidateQueries({ queryKey: ['pools', 'ranking'] });
      addNotification({
        type: 'success',
        message: response.data?.message || 'Rankings recalculados!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao recalcular rankings',
        duration: 5000,
      });
    },
  });
}
